'use client'

import React, { HTMLAttributes } from 'react'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import { cn } from '@/lib/css-utils'
import { useCustomPush } from '@/lib/client/navigation'
import { Link } from '@/components/ui/deprecated-link'
import { usePathname, useSearchParams } from 'next/navigation'

interface PaginationProps extends HTMLAttributes<HTMLDivElement> {
  currentPage: number
  totalPages: number
  maxPaginationOptionsDisplay?: number
  // when returning false in onPageChange will prevent the change
  onPageChange?: (page: number) => boolean
}

function getPageSalaryHref(
  searchParams: URLSearchParams,
  page: number,
  pathname: string,
) {
  const pageParams = searchParams
  pageParams.set('page', page.toString())
  return `${pathname}?${pageParams.toString()}`
}

export function SalaryPagination({
  currentPage,
  totalPages,
  maxPaginationOptionsDisplay = 1,
  onPageChange,
  className,
}: PaginationProps) {
  const pathname = usePathname()
  const params = useSearchParams()
  const searchParams = new URLSearchParams(params)
  const push = useCustomPush()

  const pageOptions = Array.from(
    { length: totalPages },
    // + 1 so is not index 0 (API first page is 1)
    (_, index) => index + 1,
  )

  const handlePageChange = (
    page: number,
    evt: React.MouseEvent<HTMLAnchorElement>,
  ) => {
    const result = !onPageChange || onPageChange?.(page)
    if (!result) {
      evt.preventDefault()
      return
    }
    // close details panels when changing page
    push({ page: page.toString(), salaryId: '' })
  }

  const previousPageIndex = Math.max(currentPage - 1, 0)
  const nextPageIndex = Math.min(currentPage + 1, totalPages)
  const previousPageHref = getPageSalaryHref(
    searchParams,
    previousPageIndex,
    pathname,
  )
  const nextPageHref = getPageSalaryHref(searchParams, nextPageIndex, pathname)
  const firstPageHref = getPageSalaryHref(searchParams, 1, pathname)
  const lastPageHref = getPageSalaryHref(searchParams, totalPages, pathname)

  return (
    <nav
      className={cn('flex justify-center', className)}
      aria-label="Pagination"
    >
      {/* Display first option once padded don't include it */}
      <ul
        className="inline-flex items-center rounded-3xl border border-table-border bg-table-pagination-bg \
          shadow-sm h-fit overflow-hidden"
      >
        <li className="flex items-center">
          <Link
            size="sm"
            href={previousPageHref}
            onClick={(evt) => handlePageChange(previousPageIndex, evt)}
            rel="prev"
            className={cn(
              'px-2 sm:px-4 text-sm font-medium text-copy-primary disabled:opacity-50 rounded-r-none',
              'border-r border-table-border',
              { hidden: currentPage === 1 },
            )}
          >
            <ArrowLeft className="mr-2 h-5 w-5 text-table-pagination-icon" />
            <span className="hidden sm:block">Prev</span>
          </Link>
        </li>
        {currentPage - 1 > maxPaginationOptionsDisplay && (
          <>
            <li>
              <Link
                size="sm"
                href={firstPageHref}
                className="px-2 sm:px-4 py-2 text-sm font-medium text-copy-primary border-r border-table-border"
                onClick={(evt) => handlePageChange(1, evt)}
              >
                1
              </Link>
            </li>
            <li className="pl-2 text-copy-primary">...</li>
          </>
        )}

        {/*Display MAX_PAGINATION_OPTIONS_DISPLAY options on each side of the current page*/}
        {pageOptions
          .slice(
            Math.max(0, currentPage - maxPaginationOptionsDisplay - 1),
            Math.min(currentPage + maxPaginationOptionsDisplay, totalPages),
          )
          .map((pageNumber) => {
            const pageHref = getPageSalaryHref(
              searchParams,
              pageNumber,
              pathname,
            )
            return (
              <li key={pageNumber}>
                <Link
                  size="sm"
                  href={pageHref}
                  className={`px-4 py-2 text-sm font-medium ${
                    currentPage === pageNumber
                      ? 'bg-table-pagination-selected-bg text-copy-primary'
                      : 'text-copy-primary'
                  } border-r border-solid border-table-border rounded-none`}
                  onClick={(evt) => handlePageChange(pageNumber, evt)}
                  aria-current={currentPage === pageNumber ? 'page' : undefined}
                >
                  {pageNumber}
                </Link>
              </li>
            )
          })}

        {/*Display last option once padded options don't include it*/}
        {currentPage < totalPages - maxPaginationOptionsDisplay && (
          <>
            <li className="px-2 text-copy-primary">...</li>
            <li>
              <Link
                size="sm"
                href={lastPageHref}
                className="px-4 text-sm font-medium text-copy-primary border-r border-table-border"
                onClick={(evt) => handlePageChange(totalPages, evt)}
              >
                {totalPages}
              </Link>
            </li>
          </>
        )}

        <li>
          <Link
            size="sm"
            href={nextPageHref}
            className={cn(
              'px-2 sm:px-4 py-2 text-sm font-medium text-copy-primary disabled:opacity-50 rounded-l-none',
              { hidden: currentPage === totalPages },
            )}
            onClick={(evt) => handlePageChange(nextPageIndex, evt)}
            rel="next"
          >
            <span className="hidden sm:block">Next</span>
            <ArrowRight className="ml-2 h-5 w-5 text-table-pagination-icon" />
          </Link>
        </li>
      </ul>
    </nav>
  )
}
